"use client";

import { format } from "date-fns";
import { memo, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateUtils } from "@/feature/flight/search/lib/form";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  value?: Date | null;
  onChange: (date: Date | null | undefined) => void;
  label?: string;
  placeholder?: string;
  disabledDate?: (date: Date) => boolean;
  isError?: boolean;
  minDate?: Date;
}

export const DatePicker = memo(function DatePicker({
  value,
  onChange,
  label,
  placeholder = "Pick a date",
  disabledDate,
  isError = false,
  minDate,
}: DatePickerProps) {
  const [open, onOpenChange] = useState(false);
  const handleSelect = (date: Date | null | undefined) => {
    if (date) {
      onChange(date);
    }
    onOpenChange(false);
  };

  const defaultMonth = useMemo(() => {
    return DateUtils.getDefaultMonth(minDate, value ?? undefined);
  }, [minDate, value]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          aria-invalid={isError}
          variant="ghost"
          aria-label={
            value
              ? `${label ?? "Date"}: ${format(value, "MMMM dd, yyyy")}`
              : label ?? placeholder
          }
          className={cn(
            "h-14 w-full rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40 sm:h-[56px]",
            !value && !isError && "text-muted-foreground",
          )}
        >
          {value ? (
            <span className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
              <span className="text-[1.5rem] font-bold leading-none tracking-tight text-foreground sm:text-[1.75rem]">
                {format(value, "dd")}
              </span>
              <span className="h-9 w-px bg-border" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground/85">
                  {format(value, "MMMM")}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {format(value, "EEEE, yyyy")}
                </span>
              </span>
            </span>
          ) : (
            <span className="min-w-0 text-left w-full">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {label}
              </span>
              <span className="block truncate text-sm text-muted-foreground">
                {placeholder}
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={handleSelect}
          disabled={disabledDate}
          defaultMonth={defaultMonth}
          captionLayout="dropdown"
          fromYear={new Date().getFullYear()}
          toYear={new Date().getFullYear() + 2}
        />
      </PopoverContent>
    </Popover>
  );
});
